export interface LegalInline {
  text: string;
  bold?: boolean;
  href?: string;
}

export interface LegalListItem {
  inlines: LegalInline[];
  sublist?: LegalList;
}

export interface LegalList {
  ordered: boolean;
  items: LegalListItem[];
}

export type LegalBlock =
  | { type: 'heading'; id: string; level: 1 | 2; text: string }
  | { type: 'paragraph'; inlines: LegalInline[] }
  | ({ type: 'list' } & LegalList)
  | { type: 'table'; rows: LegalInline[][][] };

export interface LegalTocItem {
  id: string;
  text: string;
  level: 1 | 2;
}

export interface LegalDocumentModel {
  blocks: LegalBlock[];
  toc: LegalTocItem[];
}

const hasBoldMark = (node: any): boolean => Array.isArray(node?.marks) && node.marks.some((mark) => mark?.type === 'bold');

function collectInlines(node: any): LegalInline[] {
  const inlines: LegalInline[] = [];

  const walk = (current: any, href?: string) => {
    if (!current || typeof current !== 'object') return;

    if (current.nodeType === 'text') {
      if (current.value) {
        inlines.push({ text: current.value, bold: hasBoldMark(current) || undefined, href });
      }
      return;
    }

    if (current.nodeType === 'hyperlink') {
      const uri = current?.data?.uri;
      (current.content ?? []).forEach((child: any) => walk(child, uri));
      return;
    }

    (current.content ?? []).forEach((child: any) => walk(child, href));
  };

  walk(node);
  return inlines;
}

const inlinesText = (inlines: LegalInline[]): string => inlines.map((inline) => inline.text).join('');

function isSectionHeadingParagraph(node: any): boolean {
  if (node?.nodeType !== 'paragraph') return false;

  const texts = (node.content ?? []).filter((child: any) => child?.nodeType === 'text' && child.value?.trim());
  if (texts.length === 0) return false;
  if (!texts.every((child: any) => hasBoldMark(child))) return false;

  const text = texts
    .map((child: any) => child.value)
    .join('')
    .trim();
  return Boolean(text) && !text.endsWith(':');
}

function parseList(node: any): LegalList {
  const items: LegalListItem[] = (node.content ?? [])
    .filter((item: any) => item?.nodeType === 'list-item')
    .map((item: any) => {
      // Only the item's own paragraphs belong to the item text; nested lists stay nested.
      const inlines = (item.content ?? [])
        .filter((child: any) => child?.nodeType === 'paragraph')
        .flatMap((paragraph: any) => collectInlines(paragraph));
      const nested = (item.content ?? []).find((child: any) => child?.nodeType === 'unordered-list' || child?.nodeType === 'ordered-list');
      return { inlines, sublist: nested ? parseList(nested) : undefined };
    })
    .filter((item: LegalListItem) => inlinesText(item.inlines).trim() || item.sublist);

  return { ordered: node?.nodeType === 'ordered-list', items };
}

export function parseLegalDocument(document: any): LegalDocumentModel {
  const blocks: LegalBlock[] = [];
  const toc: LegalTocItem[] = [];
  let headingIndex = 0;

  const pushHeading = (level: 1 | 2, text: string) => {
    const id = `legal-section-${headingIndex++}`;
    blocks.push({ type: 'heading', id, level, text });
    toc.push({ id, text, level });
  };

  (document?.content ?? []).forEach((node: any) => {
    switch (node?.nodeType) {
      case 'heading-1':
        pushHeading(1, inlinesText(collectInlines(node)).trim());
        break;
      case 'heading-2':
        pushHeading(2, inlinesText(collectInlines(node)).trim());
        break;
      case 'paragraph': {
        const inlines = collectInlines(node);
        const text = inlinesText(inlines);
        if (!text.trim()) break;
        if (isSectionHeadingParagraph(node)) {
          pushHeading(2, text.trim());
          break;
        }
        blocks.push({ type: 'paragraph', inlines });
        break;
      }
      case 'unordered-list':
      case 'ordered-list': {
        const list = parseList(node);
        if (list.items.length > 0) {
          blocks.push({ type: 'list', ...list });
        }
        break;
      }
      case 'table': {
        const rows = (node.content ?? [])
          .filter((row: any) => row?.nodeType === 'table-row')
          .map((row: any) => (row.content ?? []).filter((cell: any) => cell?.nodeType === 'table-cell').map((cell: any) => collectInlines(cell)));
        if (rows.length > 0) {
          blocks.push({ type: 'table', rows });
        }
        break;
      }
      default:
        break;
    }
  });

  return { blocks, toc };
}
